import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';

// Initialize Stripe outside component to avoid recreation
let stripePromise = null;

const CheckoutForm = ({ clientSecret, onSuccess, amount }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: window.location.origin + '/rides'
        }
      });

      if (error) {
        toast.error(error.message || 'Payment failed');
        setLoading(false);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        toast.success('Payment successful!');
        onSuccess(paymentIntent.id);
      }
    } catch (err) {
      toast.error('Payment processing error');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Card className="bg-gray-50">
        <CardContent className="pt-6">
          <div className="mb-4">
            <p className="text-sm text-gray-600">Amount to pay</p>
            <p className="text-2xl font-bold text-green-600">${amount.toFixed(2)}</p>
          </div>
          <PaymentElement />
        </CardContent>
      </Card>
      <Button 
        type="submit" 
        disabled={!stripe || loading} 
        className="w-full"
      >
        {loading ? 'Processing...' : `Pay $${amount.toFixed(2)}`}
      </Button>
    </form>
  );
};

const StripeCheckout = ({ amount, rideDetails, onSuccess, isSubscription = false, clientSecret: providedClientSecret }) => {
  const [clientSecret, setClientSecret] = useState(providedClientSecret || '');
  const [loading, setLoading] = useState(!providedClientSecret);

  useEffect(() => {
    if (!providedClientSecret) {
      initializeStripe();
    }
  }, [providedClientSecret]);

  const initializeStripe = async () => {
    try {
      // Fetch publishable key
      const configResponse = await axios.get(`${API}/stripe-config`);
      const { publishableKey } = configResponse.data;
      
      if (!stripePromise) {
        stripePromise = loadStripe(publishableKey);
      }

      // Create payment intent (only if not a subscription with provided secret)
      if (!isSubscription && !providedClientSecret) {
        const response = await axios.post(`${API}/create-payment-intent`, {
          amount,
          ride_details: rideDetails
        });
        setClientSecret(response.data.client_secret);
      }
      
      setLoading(false);
    } catch (error) {
      toast.error('Failed to initialize payment');
      console.error('Stripe initialization error:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <p>Loading payment form...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!clientSecret) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <p className="text-red-600">Failed to load payment form</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const options = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#16a34a',
      }
    }
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <CheckoutForm 
        clientSecret={clientSecret} 
        onSuccess={onSuccess}
        amount={amount}
      />
    </Elements>
  );
};

export default StripeCheckout;
